import { ChangeEvent, useContext, useState } from "react";
import Flex from "../../components/Flex";
import { Box, Button, FormControlLabel, Paper, Switch } from "@mui/material";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  beginDraft,
  selectDraftMode,
  selectLensEditorForm,
  selectLensEditorLens,
  selectRules,
  updateLive,
} from "redux/slices/lensEditor";
import gqlClient, { queryOrFail } from "gqlClient";
import { P } from "theme/typography";
import { ClearStaleLensVersionsDocument } from "generated/graphql";
import { useSnackbar } from "notistack";
import PatternConfetti from "../../components/PatternConfetti";
import useAppDispatchWithSnackbar from "../../helpers/hooks/useAppDispatchWithSnackbar";
import { backend } from "Backend";
import triggerDownload from "helpers/triggerDownload";
import { stripNull } from "helpers/coll";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";

const LensEditorHeader = () => {
  const { enqueueSnackbar } = useSnackbar();

  const lens = useAppSelector(selectLensEditorLens);
  const lensForm = useAppSelector(selectLensEditorForm);
  const draftVersion = lens.draftVersion;
  const allRules = useAppSelector(selectRules);
  const fullCount = allRules.length;
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [disableLensDifferences, setDisableLensDifferences] = useState<boolean>(false);
  const confirmCtx = useContext(ConfirmationDialogContext);
  const lensFamily = stripNull(lens.lensFamily);

  const lensId = lens.id;

  const dispatch = useAppDispatch();
  const snackpatch = useAppDispatchWithSnackbar();

  const isDraftMode = useAppSelector(selectDraftMode);

  const handleBeginDraft = () => {
    dispatch(beginDraft({ lensId }));
  };

  const handleToggleLive = (event: ChangeEvent<HTMLInputElement>) => {
    const live = event.target.checked;
    const message = live ? "Form is now live" : "Form is no longer live";

    snackpatch(updateLive({ live, formId: lensForm.id }), {
      successMessage: message,
      errorMessage: "Unable to update live status",
    }).then(() => {
      if (live) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);
      }
    });
  };

  const handleDownloadFullArchive = async () => {
    const result = await backend.getFullLensArchive(lensId);
    backend.mapSuccess(result, (response) => {
      triggerDownload(new Blob([response.pdfBlob]), response.fileName);
    });
  };

  const handleSendLensFamilyDifferenceReport = async () => {
    if (lensFamily !== undefined) {
      confirmCtx.confirm({
        title: "Send Lens Family Differences",
        prompt: <P variant="sm">Find differences within lens family and send support email?</P>,
        callback: async () => {
          setDisableLensDifferences(true);
          await backend
            .sendLensFamilyDifferencesReport(lensFamily)
            .then((_) => {
              enqueueSnackbar(`Successfully sent lens difference report to support@patterndata.io`, {
                variant: "success",
              });
            })
            .catch((e) => {
              console.error(e);
              enqueueSnackbar(`Unable to generate lens difference`, { variant: "error" });
            })
            .finally(() => {
              setDisableLensDifferences(false);
            });
        },
      });
    }
  };

  const handleClearStaleRules = () => {
    queryOrFail(gqlClient.mutation(ClearStaleLensVersionsDocument, { lensId }).toPromise())
      .then((resp) => {
        const clearedCount = resp.lens.clearStaleVersions;
        enqueueSnackbar(`Successfully cleared ${clearedCount} stale lens rules!`, {
          variant: "success",
        });
      })
      .catch(() => {
        enqueueSnackbar(`Operation failed`, {
          variant: "error",
        });
      });
  };

  return (
    <Flex
      sx={{
        display: "flex",
        maxHeight: "100px",
      }}
      component={Paper}
      gridArea="header"
      p={2}>
      {showConfetti && <PatternConfetti />}
      <Flex flexDirection="row">
        <Box>
          <P sx={{ mr: 1, color: "grey.400" }}>{lens.description}</P>
          <P sx={{ mr: 1, color: "grey.400 " }}>Rules: {fullCount}</P>
        </Box>
        <P sx={{ mr: 1, color: "grey.400" }}>
          v{lens.version} {draftVersion && " -> " + draftVersion}
        </P>

        {!isDraftMode && (
          <Button size="small" onClick={handleBeginDraft}>
            Begin Draft
          </Button>
        )}
        <Button disabled size="small" onClick={handleClearStaleRules}>
          Clear Stale Rules
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {lensFamily !== undefined && (
          <Button disabled={disableLensDifferences} onClick={handleSendLensFamilyDifferenceReport}>
            Send Lens Differences
          </Button>
        )}
        <Button onClick={handleDownloadFullArchive}>Download Full Lens Archive</Button>
        <FormControlLabel
          id="confetti-holder"
          control={<Switch checked={lensForm.live} onChange={handleToggleLive} />}
          label="Live Status"
        />
      </Flex>
    </Flex>
  );
};

export default LensEditorHeader;
